import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { GuidService } from './guid-service';
import { SubscribableService } from './subscribable-service';

const notificationMessages = defineMessages({
  successTitle: {
    description: 'Title for a success message',
    defaultMessage: 'Success',
    id: '1cSjsn',
  },
  errorTitle: {
    description: 'Title for an error message',
    defaultMessage: 'Error',
    id: '0kLO2P',
  },
  errorContent: {
    description: 'Content for an error message',
    defaultMessage: 'Please try again.',
    id: 'JOOJke',
  },
});

export type NotificationConfig = {
  title?: React.ReactNode;
  content?: React.ReactNode;
  notificationId?: string;
  notificationType?: NotificationType;
  dontHide?: boolean;
  duration?: number;
  progressPercentage?: number;
  onClose?: () => void;
  /** Set internally by the service after showNotification is called */
  id?: string;
  /** Set internally by the service after showNotification is called */
  elementId?: string;
  [key: string]: unknown;
};

const notifications: Record<string, NotificationConfig> = {};
const notificationsToRemove: Record<string, NotificationConfig> = {};

/*
Some guidelines for choosing an appropriate duration:
1) user-servicable errors (i.e. errors where the user has to fix something) => NO DURATION
2) we know exactly what happen errors and the seller can't do anything => LONG
3) we have no idea what happened => SHORT OR MEDIUM
*/
export const NOTIFICATION_DURATIONS = {
  SHORT: 3000,
  MEDIUM: 10000,
  LONG: 30000,
};

const DEFAULT_NOTIFICATION_DURATION = 30000;
const NOTIFICATION_HIDE_ANIMATION_DURATION = 600;

export const NOTIFICATION_TYPE_SUCCESS = 'success' as const;
export const NOTIFICATION_TYPE_WARNING = 'warning' as const;
export const NOTIFICATION_TYPE_INFO = 'info' as const;
export const NOTIFICATION_TYPE_ERROR = 'error' as const;
export const NOTIFICATION_TYPES = [
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_WARNING,
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_ERROR,
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

class NotificationServiceClass extends SubscribableService {
  createNotificationId(): string {
    return GuidService.newGuid();
  }

  getNotificationElementId(notificationId: string): string {
    return `new-notification-${notificationId}`;
  }

  showNotification(notificationConfig: NotificationConfig): string {
    const notificationId = notificationConfig.notificationId || this.createNotificationId();
    // this code relies on the notificationConfig being the same object reference
    // oxlint-disable-next-line no-param-reassign
    notificationConfig.id = notificationId;
    // oxlint-disable-next-line no-param-reassign
    notificationConfig.elementId = this.getNotificationElementId(notificationId);
    notifications[notificationId] = notificationConfig;
    this.onChange();
    if (!notificationConfig.dontHide && !notificationConfig.duration) {
      // oxlint-disable-next-line no-param-reassign
      notificationConfig.duration = DEFAULT_NOTIFICATION_DURATION;
    }
    if (!notificationConfig.dontHide && notificationConfig.progressPercentage === undefined) {
      setTimeout(() => this.removeNotification(notificationId), notificationConfig.duration);
    }
    return notificationId;
  }

  updateNotification(notificationId: string, notificationConfig: Partial<NotificationConfig>): void {
    const existingNotification = notifications[notificationId];
    if (existingNotification === undefined) {
      return;
    }
    notifications[notificationId] = {
      ...existingNotification,
      ...notificationConfig,
    };
    const newNotification = notifications[notificationId];
    if (!newNotification.duration && !newNotification.dontHide) {
      newNotification.duration = DEFAULT_NOTIFICATION_DURATION;
    }

    if (newNotification.progressPercentage !== undefined && newNotification.progressPercentage >= 100) {
      setTimeout(() => this.removeNotification(notificationId), newNotification.duration);
    }
    if (!newNotification.dontHide && existingNotification.dontHide) {
      setTimeout(() => this.removeNotification(notificationId), newNotification.duration);
    }
    this.onChange();
  }

  removeNotification = (notificationId: string): void => {
    notificationsToRemove[notificationId] = notifications[notificationId];
    this.onChange();
    setTimeout(() => {
      this.completelyRemoveNotification(notificationId);
    }, NOTIFICATION_HIDE_ANIMATION_DURATION);
  };

  completelyRemoveNotification(notificationId: string): void {
    const notificationConfig = notifications[notificationId];
    if (notificationConfig && notificationConfig.onClose) {
      notificationConfig.onClose();
    }
    Reflect.deleteProperty(notifications, notificationId);
    Reflect.deleteProperty(notificationsToRemove, notificationId);
    this.onChange();
  }

  getNotifications(): Record<string, NotificationConfig> {
    return notifications;
  }

  getNotificationsToRemove(): Record<string, NotificationConfig> {
    return notificationsToRemove;
  }

  getProgressBarThemeFromNotificationType(notificationType: NotificationType): string {
    switch (notificationType) {
      case NOTIFICATION_TYPE_SUCCESS:
        return 'create';
      case NOTIFICATION_TYPE_ERROR:
        return 'danger';
      case NOTIFICATION_TYPE_WARNING:
        return 'warning';
      default:
        return 'primary';
    }
  }

  clearNotifications(): void {
    Object.keys(notifications).forEach(this.removeNotification);
  }

  showGenericSuccessNotification = (
    title: React.ReactNode = <FormattedMessage {...notificationMessages.successTitle} />,
    content?: React.ReactNode,
  ): string => {
    return this.showNotification({
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      title,
      content,
      duration: NOTIFICATION_DURATIONS.SHORT,
    });
  };

  showGenericFailureNotification = (
    title: React.ReactNode = <FormattedMessage {...notificationMessages.errorTitle} />,
    content: React.ReactNode = <FormattedMessage {...notificationMessages.errorContent} />,
  ): string => {
    return this.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title,
      content,
      duration: NOTIFICATION_DURATIONS.SHORT,
    });
  };
}

export const NotificationService = new NotificationServiceClass();
